import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, API_URL } from "../api/client";
import { identifySentryUser, clearSentryUser } from "../services/sentry";
import { autenticarRealtime, limparAuthRealtime } from "../realtimeClient";
import { track } from "../lib/pixel";

// AuthContext mantém o usuário logado e expõe as ações de auth (login/registro/logout/etc).
// Bootstrap valida o token contra o backend antes de aceitar como logado — se token expirou,
// o user fica deslogado em vez de "fantasma" com dados stale do localStorage.
//
// Em falha de rede no bootstrap, cai pro cache do localStorage (offline-friendly).
// Login detecta troca de usuário e limpa contexto + cache do SW antes de setar o novo.
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      identifySentryUser(user);
      // Autentica Realtime com JWT custom - habilita subscriptions em tabelas
      // com RLS (pedidos, ofertas etc). Falha gracioso: se backend nao tem
      // SUPABASE_JWT_SECRET, continua com anon key + polling fallback.
      autenticarRealtime().catch(() => {});
    } else {
      clearSentryUser();
      limparAuthRealtime();
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        api.loadToken();
        const tokenAtual = localStorage.getItem('toalugando_token');
        if (!tokenAtual) {
          localStorage.removeItem('toalugando_user');
          setLoading(false);
          return;
        }
        try {
          const r = await api.fetch('/usuarios/me');
          if (r.ok) {
            const u = await r.json();
            localStorage.setItem('toalugando_user', JSON.stringify(u));
            setUser(u);
          } else if (r.status === 401 || r.status === 403) {
            localStorage.removeItem('toalugando_user');
            api.clearTokens();  // setTokens(null) gravava o texto "null" como token
          } else {
            // Erro do SERVIDOR não desloga: antes um 500 aqui apagava a
            // sessão (auditoria 29/10, A8-05). Segue com o usuário salvo.
            const savedUser = localStorage.getItem('toalugando_user');
            if (savedUser) {
              try { setUser(JSON.parse(savedUser)); } catch {}
            }
          }
        } catch (e) {
          if (e?.message === 'SESSAO_EXPIRADA') {
            // Sessão de fato vencida: sair. Antes a tela abria "logada" com o
            // usuário salvo e toda chamada falhava (A8-05).
            localStorage.removeItem('toalugando_user');
            api.clearTokens();
          } else {
            const savedUser = localStorage.getItem('toalugando_user');
            if (savedUser) {
              try { setUser(JSON.parse(savedUser)); } catch {}
            }
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, senha) => {
    if (!email || typeof email !== 'string' || !senha) {
      throw new Error('Email e senha são obrigatórios');
    }
    try {
      const res = await api.post('/auth/login', { email, senha });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Email ou senha incorretos');
      if (data.mfa_required) {
        const err = new Error('mfa_required');
        err.mfa = { challenge_token: data.challenge_token, metodo: data.metodo };
        throw err;
      }
      api.setTokens(data.access_token, data.refresh_token);
      const userData = data.usuario;
      try {
        const prevUserRaw = localStorage.getItem('toalugando_user');
        const prevUser = prevUserRaw ? JSON.parse(prevUserRaw) : null;
        if (prevUser?.id && prevUser.id !== userData.id) {
          localStorage.removeItem('toalugando_op_ctx');
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
          }
        }
      } catch (e) {}
      localStorage.setItem('toalugando_user', JSON.stringify(userData));
      setUser(userData);
      try {
        const { registrarFingerprint } = await import('../services/fingerprint');
        registrarFingerprint('login');
      } catch (e) {}
      return userData;
    } catch (e) {
      if (e.mfa) throw e;
      if (e instanceof TypeError) {
        throw new Error('Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.');
      }
      throw new Error(e.message || 'Email ou senha incorretos');
    }
  };

  const concluir2FA = async (challenge_token, codigo) => {
    const res = await api.post('/auth/login-2fa', { challenge_token, codigo });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Código inválido.');
    api.setTokens(data.access_token, data.refresh_token);
    try {
      const prev = JSON.parse(localStorage.getItem('toalugando_user') || 'null');
      if (prev?.id && prev.id !== data.usuario?.id) localStorage.removeItem('toalugando_op_ctx');
    } catch (e) {}
    localStorage.setItem('toalugando_user', JSON.stringify(data.usuario));
    setUser(data.usuario);
    return data.usuario;
  };

  const registro = async (dados) => {
    try {
      const codigoIndicacao = (typeof window !== 'undefined' ? localStorage.getItem('toalugando_ref') : null) || null;
      // Inclui cpf/cnpj/tipo quando o user escolheu PJ/freteiro no signup -
      // antes esses campos eram coletados na TelaRegistro mas descartados
      // silenciosamente (UsuarioCreate nao aceitava). Agora persiste pra
      // habilitar onboarding direcionado depois.
      const docDigitos = (dados.cpfCnpj || '').replace(/\D/g, '');
      const eCnpj = docDigitos.length === 14;
      const ePf = docDigitos.length === 11;
      // Convite de LOCADOR (oferta ativa): vai no payload pro backend consumir
      // no próprio signup — o localStorage não viaja entre navegadores, e o
      // fluxo real é abrir o link no navegador do WhatsApp e confirmar o
      // e-mail no Chrome. O resgate pós-login continua como fallback.
      const conviteLocador = (typeof window !== 'undefined'
        ? localStorage.getItem('toalugando_convite_locador') : null) || null;
      const res = await api.post('/auth/registro', {
        email: dados.email,
        senha: dados.senha,
        nome: dados.nome,
        telefone: dados.telefone || null,
        codigo_indicacao: codigoIndicacao,
        convite_locador: conviteLocador,
        tipo: dados.tipo || null,
        cpf: ePf ? docDigitos : null,
        cnpj: eCnpj ? docDigitos : null,
        // "Não sou robô" — só existe quando o Turnstile está ligado
        ...(dados.turnstileToken ? { turnstile_token: dados.turnstileToken } : {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro no registro');

      // Meta Pixel — conversao de cadastro. Disparado AQUI, depois do res.ok,
      // e nao no onClick do botao: a conta so existe quando o backend confirma.
      // Este ponto cobre as DUAS saidas de sucesso abaixo (a que exige
      // confirmacao por e-mail e a que ja devolve token), sem duplicar.
      track('CompleteRegistration', { content_name: 'cadastro_toalugando', status: true });

      if (data.requer_confirmacao_email) {
        try {
          const { getVisitorId } = await import('../services/fingerprint');
          const vid = await getVisitorId();
          if (vid) {
            await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/usuarios/registro-fingerprint`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: dados.email, visitor_id: vid }),
            }).catch(() => {});
          }
        } catch (e) {}
        return { requer_confirmacao_email: true, email: data.email, mensagem: data.mensagem };
      }

      if (data.access_token) {
        api.setTokens(data.access_token, data.refresh_token);
        const userData = data.usuario;
        try {
          const prev = JSON.parse(localStorage.getItem('toalugando_user') || 'null');
          if (prev?.id && prev.id !== userData?.id) localStorage.removeItem('toalugando_op_ctx');
        } catch (e) {}
        localStorage.setItem('toalugando_user', JSON.stringify(userData));
        setUser(userData);
        try {
          const { registrarFingerprint } = await import('../services/fingerprint');
          registrarFingerprint('signup');
        } catch (e) {}
        return userData;
      }
      return { requer_confirmacao_email: true, email: dados.email };
    } catch (e) {
      throw new Error(e.message || 'Erro ao criar conta');
    }
  };

  const loginOAuth = async (accessToken, refreshToken) => {
    const codigoIndicacao = (typeof window !== 'undefined' ? localStorage.getItem('toalugando_ref') : null) || null;
    const res = await fetch(`${API_URL}/api/auth/oauth-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: accessToken, refresh_token: refreshToken || null, codigo_indicacao: codigoIndicacao }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Erro no login OAuth');
    // Conta com 2FA: o Google não dispensa o código do app autenticador
    if (data.mfa_required) {
      const err = new Error('mfa_required');
      err.mfa = { challenge_token: data.challenge_token, metodo: data.metodo };
      throw err;
    }
    api.setTokens(data.access_token, data.refresh_token || refreshToken);
    const userData = data.usuario;
    // Troca de usuário no mesmo navegador: limpa o contexto "Operando como"
    // (chave global) pra o novo user não herdar o empresa_id do anterior — senão
    // seus itens/pedidos PF somem. O login por senha já fazia isso; faltava no
    // OAuth/Google, que é justamente o caminho que todos usam.
    try {
      const prev = JSON.parse(localStorage.getItem('toalugando_user') || 'null');
      if (prev?.id && prev.id !== userData.id) localStorage.removeItem('toalugando_op_ctx');
    } catch (e) {}
    localStorage.setItem('toalugando_user', JSON.stringify(userData));
    setUser(userData);
    // Meta Pixel — conversao de cadastro pelo GOOGLE. Este caminho responde
    // pela maioria esmagadora dos cadastros (vide o comentario acima e o
    // "quase todo cadastro e social" em auth.py). Sem este disparo o evento
    // ficava INATIVO no Gerenciador, porque so o cadastro por e-mail estava
    // instrumentado. content_name diferente pra dar pra separar os dois
    // caminhos no Gerenciador de Eventos.
    if (data.novo_usuario) {
      track('CompleteRegistration', { content_name: 'cadastro_google', status: true });
    }
    return { usuario: userData, novo_usuario: !!data.novo_usuario };
  };

  const logout = async () => {
    localStorage.removeItem('toalugando_user');
    localStorage.removeItem('toalugando_perfil');
    localStorage.removeItem('toalugando_kyc');
    localStorage.removeItem('toalugando_op_ctx');
    // NOTA: carrinho NÃO é mais apagado aqui — está vinculado ao user_id (toalugando_cart_<uid>)
    api.clearTokens();
    setUser(null);
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      }
    } catch (e) {}
  };

  const atualizarPerfil = async (dados) => {
    try {
      const res = await api.put('/usuarios/me', dados);
      const userData = await res.json();
      if (!res.ok) throw new Error(userData.detail || 'Erro ao atualizar');
      localStorage.setItem('toalugando_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (e) {
      const updated = { ...user, ...dados };
      localStorage.setItem('toalugando_user', JSON.stringify(updated));
      setUser(updated);
      return updated;
    }
  };

  const setUserPersistido = (updater) => {
    setUser((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        if (next) localStorage.setItem('toalugando_user', JSON.stringify(next));
        else localStorage.removeItem('toalugando_user');
      } catch (e) {}
      return next;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user, loading, login, concluir2FA, loginOAuth, registro, logout,
        atualizarPerfil, setUser: setUserPersistido, isLogado: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook utilitário: protege uma ação que precisa de login.
// Uso: const proteger = useRequireAuth(nav); ... onClick={() => proteger(() => doStuff())}
export const useRequireAuth = (nav) => {
  const { isLogado } = useAuth();
  return useCallback((acaoSeLogado) => {
    if (!isLogado) { nav?.('login'); return; }
    if (typeof acaoSeLogado === 'function') acaoSeLogado();
  }, [isLogado, nav]);
};
